import { CrossFarmDashboardSearchModel } from '../../../../../shared/model/cross-farm-dashboard/CrossFarmDashboardSearchModel';
import { CropUtils } from '../../../../../shared/utils/CropUtils';
import { CROPS } from '../../../../../js/resources/SessionObjects';

export const localStorageFilterKey = 'CFD_FILTER_GROUP_GENERAL';

/**
 * @class GeneralFilterState
 * @extends Backbone.Model
 */
export class GeneralFilterGroupState extends Backbone.Model {

    get defaults () {

        return {
            available: {
                farms: new Backbone.Collection(),
                users: new Backbone.Collection(),
                crops: new Backbone.Collection(),
            },

            filtered: {
                farms: new Backbone.Collection(),
                users: new Backbone.Collection(),
                crops: new Backbone.Collection(),
            },

            headFilter: new Backbone.Model(this.getDefaultHeadFilter()),

            serializedFilters: this.getDefaultSerializedFilter(),
        };
    }

    getDefaultHeadFilter() {
        return {
            isHealthyField: false,
        };
    }

    getDefaultSerializedFilter() {
        return {
            farms: [],
            users: [],
            crops: [],
            headFilter: this.getDefaultHeadFilter(),
        };
    }

    initialize () {

        const {
            available,
            filtered,
        } = this.attributes;

        const comp = 'name';

        Object.keys(available).forEach( (key) => {
            available[key].comparator = comp;
        });

        Object.keys(filtered).forEach( (key) => {
            filtered[key].comparator = comp;
        });
    }

    fetchData () {

        const {
            available,
            filtered,
        } = this.attributes;

        const filterData = new CrossFarmDashboardSearchModel();
        const ownersAndFarmsFetched = $.Deferred();

        filterData
            .request(CrossFarmDashboardSearchModel.getFarmUserFilter())
            .done((data) => {

                let uniqueFarmOwners = _.uniq(_.map(data.hits.hits, (item) => item._source.farmOwnerId));

                $.ajax({
                    url: (API_PREFIX + `/users/users?uuid=${uniqueFarmOwners}`),
                    type: 'GET',
                    dataType: 'json',
                    contentType: 'application/json'
                })
                .done((response) => {

                    const{
                        available,
                        filtered,
                    } = this.attributes;

                    const userDic = {};

                    const farmModels = data.hits.hits
                        .map((item) => {

                            const ownerUuid =item._source.farmOwnerId;
                            const ownerName = item._source.farmOwnerName;
                            let user = _.find(response, (u) => u.uuid === ownerUuid);

                            userDic[ownerUuid] = ownerName;

                            return new Backbone.Model({
                                uuid: item._source.farmUuid,
                                name: item._source.farmName,
                                ownerUuid,
                                owner: `${ownerName} - ${user? user.email : ''}`
                            });
                        });

                    available.farms.reset(farmModels);
                    filtered.farms.reset();

                    const userModels = data.aggregations.owners.buckets
                        .map((item) => {

                            let user = _.find(response, (u) => u.uuid === item.key);

                            return new Backbone.Model({
                                uuid: item.key,
                                name: userDic[item.key],
                                email: user? user.email : ''
                            });
                        });

                    available.users.reset(userModels);
                    filtered.users.reset();
                    ownersAndFarmsFetched.resolve();
                })
                .fail(() => {
                    ownersAndFarmsFetched.reject();
                });
            })
            .fail(() => {
                ownersAndFarmsFetched.reject();
            });
        
        const requestCropFilter = new Promise((resolve, reject) => {
            filterData
                .request(CrossFarmDashboardSearchModel.getCropFilter())
                .done((data) => {
                    
                    const cropTypeNameDic = CropUtils.getTypeNames(CROPS);

                    const models = data.aggregations.crops.buckets
                        .map((item) => new Backbone.Model({
                                code: item.key,
                                iconName: `df-icon-${CropUtils.dfIcon[item.key]}`,
                                name: cropTypeNameDic[item.key],
                        }));

                    available.crops.reset(models);
                    filtered.crops.reset();

                    resolve();
                })
                .fail((error) => {
                    reject(error);
                });
        });

        return new Promise((resolve, reject) => {
            Promise.all([ownersAndFarmsFetched, requestCropFilter])
                .then(() => {

                    this.initializeFilter();

                    resolve();
                })
                .catch((error) => {

                    console.error(error);

                    this.removeFilterFromLocalStorage();

                    reject(error);
                });
        });
    }

    initializeFilter () {

        let filters = localStorage.getItem(localStorageFilterKey);

        try {
            const parsed = JSON.parse(filters);

            if(parsed) {

                this.set('serializedFilters', parsed);

                const { 
                    available,
                    filtered,
                } = this.attributes;

                this.setAvailableAndFilteredItems(parsed.farms, 'uuid', available.farms, filtered.farms);
                
                this.setAvailableAndFilteredItems(parsed.users, 'uuid', available.users, filtered.users);
                
                this.setAvailableAndFilteredItems(parsed.crops, 'code', available.crops, filtered.crops);
                
                if (parsed.headFilter) {

                    this.attributes.headFilter.set(parsed.headFilter);
                }
            }
        }
        catch (e) {

            const defaultFilters = this.getDefaultSerializedFilter();

            this.set('serializedFilters', defaultFilters);

            this.removeFilterFromLocalStorage();
        }
    }

    setAvailableAndFilteredItems(array, key, availableItems, filteredItems) {
        if (array.length) {

            array.forEach((val) => {
                let model = availableItems.find({[key]: val});

                availableItems.remove(model);
                filteredItems.add(model);
            });
        }
    }

    resetAllFilters () {

        const {
            available,
            filtered,
        } = this.attributes;

        Object.keys(available).forEach((key) => {
            this.resetCollection(available[key], filtered[key]);
        });

        this.attributes.headFilter.set(this.getDefaultHeadFilter());
    }

    resetCollection(availableCollection, filteredCollection) {
        
        if(filteredCollection.length) {
            availableCollection.add(filteredCollection.models);

            filteredCollection.reset();
        }
    }

    updateFilter () {

        const {
            farms,
            users,
            crops,
        } = this.attributes.filtered;

        const serializedFilters = {
            farms: farms.pluck('uuid'),
            users: users.pluck('uuid'),
            crops: crops.pluck('code'),
            headFilter: this.attributes.headFilter.attributes,
        };

        this.set('serializedFilters', serializedFilters);

        try {
            localStorage.setItem(localStorageFilterKey, JSON.stringify(serializedFilters));
        }
        catch (e) {

            console.error('Error while saving ' + localStorageFilterKey);

            this.removeFilterFromLocalStorage();
        }
    }

    removeFilterFromLocalStorage() {
        try {
            localStorage.removeItem(localStorageFilterKey);
        }
        catch (error) {
            console.error('Error while removing ' + localStorageFilterKey);
        }
    }
}


// WEBPACK FOOTER //
// ./layout/CrossFarmDashboardLayout/Filters/GroupViews/GeneralFilterGroupView/GeneralFilterGroupState.js